<template>
  <div class="sales-quote-page">
    <a-tabs v-model="activeTab" @change="changeTab">
      <a-tab-pane key="quote" tab="报价">
        <a-alert v-if="editingQuoteId" type="info" show-icon class="editing-alert">
          <template slot="message">
            正在修改 {{ editingQuoteCode }}
            <a class="cancel-edit" @click="cancelEdit">取消修改</a>
          </template>
        </a-alert>
        <div class="quote-section">
          <div class="section-title">报价信息</div>
          <a-form
            class="simiot-compact-form"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 16 }"
          >
            <a-row>
              <a-col :span="12">
                <a-form-item label="客户名称">
                  <a-select
                    show-search
                    allow-clear
                    :filter-option="false"
                    :value="customerSelectValue"
                    placeholder="搜索已有客户，或直接填写客户名称"
                    @search="searchAgents"
                    @change="changeCustomer"
                  >
                    <a-select-option v-if="showNewCustomerOption" :value="newCustomerValue">
                      {{ customerKeyword }}
                    </a-select-option>
                    <a-select-option v-for="item in agentOptions" :key="item.id" :value="item.id">
                      {{ item.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="有效期">
                  <a-input-number
                    v-model="quote.validDays"
                    :min="1"
                    :precision="0"
                    style="width: 120px"
                  />
                  <span class="unit-text">天</span>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row>
              <a-col :span="12">
                <a-form-item label="备注">
                  <a-input
                    v-model="quote.remark"
                    allow-clear
                    placeholder="选填"
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </a-form>
        </div>

        <div class="quote-section">
          <div class="section-title">添加一行</div>
          <div class="hint section-hint">同一张报价单可以连续添加多行。换产品类型后重新点选，再点「添加本行」，SIM、eSIM、Profile、模组、CPE 可在同一张报价单。</div>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form
                class="simiot-compact-form"
                :label-col="{ span: 6 }"
                :wrapper-col="{ span: 18 }"
              >
                <a-form-item label="产品类型">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.productType"
                    @change="changeProductType"
                  >
                    <a-radio-button
                      v-for="item in productTypes"
                      :key="item.value"
                      class="card-select-box"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>

                <template v-if="draft.productType === 'sim'">
                  <a-form-item label="套餐类型">
                    <a-radio-group
                      button-style="solid"
                      :value="draft.packageKind"
                      @change="changePackageKind"
                    >
                      <a-radio-button
                        v-for="item in packageKinds"
                        :key="item.value"
                        class="card-select-box"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </a-radio-button>
                    </a-radio-group>
                  </a-form-item>

                  <template v-if="draft.packageKind === 'common'">
                    <a-form-item label="运营商">
                      <a-select
                        :value="draft.carrier"
                        placeholder="请选择"
                        @change="changeCarrier"
                      >
                        <a-select-option
                          v-for="option in carrierTypeOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                    <a-form-item label="流量场景">
                      <a-select
                        :value="draft.scene"
                        placeholder="请选择"
                        :disabled="!draft.carrier"
                        :loading="loadingTrafficSceneOptions"
                        @change="changeScene"
                      >
                        <a-select-option
                          v-for="option in trafficSceneOptions"
                          :key="option.id"
                          :value="option.id"
                        >
                          {{ option.name }}
                        </a-select-option>
                      </a-select>
                      <div v-if="selectedSceneAttrs.length" class="scene-attrs">
                        <span v-for="item in selectedSceneAttrs" :key="item.key">{{ item.label }}：{{ item.value }}</span>
                      </div>
                    </a-form-item>
                    <a-form-item label="套餐大小">
                      <a-select
                        :value="draft.size"
                        show-search
                        allow-clear
                        option-filter-prop="children"
                        placeholder="输入容量匹配"
                        :disabled="!draft.scene"
                        @change="changeSize"
                      >
                        <a-select-option
                          v-for="item in sizeOptions"
                          :key="item"
                          :value="item"
                        >
                          {{ item }}
                        </a-select-option>
                      </a-select>
                      <div v-if="draft.scene && sizeOptions.length === 0" class="hint">该场景还没有上架套餐</div>
                    </a-form-item>
                    <a-form-item v-if="needsVoiceChoice" label="语音">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.voice"
                        @change="changeVoice"
                      >
                        <a-radio-button
                          v-for="item in voiceOptions"
                          :key="item || 'none'"
                          class="card-select-box"
                          :value="item"
                        >
                          {{ item || '无语音' }}
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                    <a-form-item v-if="matchedProducts.length > 1" label="上架套餐">
                      <a-select
                        :value="draft.productId"
                        placeholder="请选择"
                        @change="changeProduct"
                      >
                        <a-select-option
                          v-for="item in matchedProducts"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.name }}（{{ item.productKind }} / {{ item.business }} / {{ item.trafficProperty }}）
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                    <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架套餐">
                      <span>{{ selectedProduct.name }}（{{ selectedProduct.productKind }} / {{ selectedProduct.business }} / {{ selectedProduct.trafficProperty }}）</span>
                    </a-form-item>
                    <a-form-item label="卡板材质">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.material"
                        @change="changeMaterial"
                      >
                        <a-radio-button
                          v-for="item in materialOptions"
                          :key="item.name"
                          class="card-select-box card-type-box"
                          :value="item.name"
                        >
                          {{ item.name }}
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                    <a-form-item label="卡片尺寸">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.shape"
                        @change="changeShape"
                      >
                        <a-radio-button
                          v-for="item in shapeOptions"
                          :key="item"
                          class="card-select-box card-shape-box"
                          :value="item"
                        >
                          {{ item }}
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                    <a-form-item label="订购周期">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.orderCycles"
                        @change="changeOrderCycles"
                      >
                        <a-radio-button
                          v-for="item in orderCycleOptions"
                          :key="item"
                          class="card-select-box"
                          :value="item"
                        >
                          {{ item }}个周期
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                  </template>

                  <template v-else-if="draft.packageKind === 'pool'">
                    <a-form-item label="总包类型">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.poolType"
                        @change="changePoolType"
                      >
                        <a-radio-button class="card-select-box" value="single">单一运营商</a-radio-button>
                        <a-radio-button class="card-select-box" value="multi">融合运营商</a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                    <a-form-item label="总包容量">
                      <a-select
                        :value="draft.size"
                        show-search
                        allow-clear
                        option-filter-prop="children"
                        placeholder="输入容量匹配"
                        :disabled="!draft.poolType"
                        @change="changeCatalogSize"
                      >
                        <a-select-option
                          v-for="item in poolSizeOptions"
                          :key="item"
                          :value="item"
                        >
                          {{ item }}
                        </a-select-option>
                      </a-select>
                      <div v-if="draft.poolType && poolSizeOptions.length === 0" class="hint">该类型还没有上架总包</div>
                    </a-form-item>
                    <a-form-item v-if="matchedProducts.length > 1" label="上架总包">
                      <a-select
                        :value="draft.productId"
                        placeholder="请选择"
                        @change="changeProduct"
                      >
                        <a-select-option
                          v-for="item in matchedProducts"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.name }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                    <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架总包">
                      <span>{{ selectedProduct.name }}</span>
                    </a-form-item>
                    <a-form-item label="订购周期">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.orderCycles"
                        @change="changeOrderCycles"
                      >
                        <a-radio-button
                          v-for="item in orderCycleOptions"
                          :key="item"
                          class="card-select-box"
                          :value="item"
                        >
                          {{ item }}个周期
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                  </template>

                  <template v-else-if="draft.packageKind === 'addon'">
                    <a-form-item label="加油包类型">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.addonType"
                        @change="changeAddonType"
                      >
                        <a-radio-button class="card-select-box" value="flow">流量包</a-radio-button>
                        <a-radio-button class="card-select-box" value="voice">语音包</a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                    <a-form-item :label="draft.addonType === 'voice' ? '语音时长' : '加油包容量'">
                      <a-select
                        :value="draft.size"
                        show-search
                        allow-clear
                        option-filter-prop="children"
                        :placeholder="draft.addonType === 'voice' ? '输入时长匹配' : '输入容量匹配'"
                        :disabled="!draft.addonType"
                        @change="changeCatalogSize"
                      >
                        <a-select-option
                          v-for="item in addonSizeOptions"
                          :key="item"
                          :value="item"
                        >
                          {{ item }}
                        </a-select-option>
                      </a-select>
                      <div v-if="draft.addonType && addonSizeOptions.length === 0" class="hint">该类型还没有上架加油包</div>
                    </a-form-item>
                    <a-form-item v-if="matchedProducts.length > 1" label="上架加油包">
                      <a-select
                        :value="draft.productId"
                        placeholder="请选择"
                        @change="changeProduct"
                      >
                        <a-select-option
                          v-for="item in matchedProducts"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.name }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                    <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架加油包">
                      <span>{{ selectedProduct.name }}</span>
                    </a-form-item>
                    <a-form-item label="订购周期">
                      <a-radio-group
                        button-style="solid"
                        :value="draft.orderCycles"
                        @change="changeOrderCycles"
                      >
                        <a-radio-button
                          v-for="item in orderCycleOptions"
                          :key="item"
                          class="card-select-box"
                          :value="item"
                        >
                          {{ item }}个周期
                        </a-radio-button>
                      </a-radio-group>
                    </a-form-item>
                  </template>
                </template>

                <template v-else-if="draft.productType === 'esim'">
                  <a-form-item label="上架eSIM">
                    <a-select
                      :value="draft.goodsId"
                      placeholder="请选择"
                      @change="changeGoods"
                    >
                      <a-select-option
                        v-for="item in esimGoods"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.name }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                  <a-form-item label="卡片材质">
                    <a-radio-group
                      button-style="solid"
                      :value="draft.material"
                      @change="changeSimpleMaterial"
                    >
                      <a-radio-button
                        v-for="item in simpleMaterials"
                        :key="item.name"
                        class="card-select-box card-type-box"
                        :value="item.name"
                      >
                        {{ item.name }}
                      </a-radio-button>
                    </a-radio-group>
                  </a-form-item>
                  <a-form-item label="卡片尺寸">
                    <a-radio-group
                      button-style="solid"
                      :value="draft.shape"
                      @change="changeShape"
                    >
                      <a-radio-button
                        v-for="item in simpleShapes"
                        :key="item"
                        class="card-select-box card-shape-box"
                        :value="item"
                      >
                        {{ item }}
                      </a-radio-button>
                    </a-radio-group>
                  </a-form-item>
                </template>

                <template v-else-if="draft.productType === 'profile'">
                  <a-form-item label="上架Profile">
                    <a-select
                      :value="draft.goodsId"
                      placeholder="请选择"
                      @change="changeGoods"
                    >
                      <a-select-option
                        v-for="item in profileGoods"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.name }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </template>

                <template v-else-if="draft.productType === 'module'">
                  <a-form-item label="模组厂商">
                    <a-select
                      :value="draft.producer"
                      placeholder="请选择"
                      @change="changeProducer"
                    >
                      <a-select-option
                        v-for="item in producerOptions"
                        :key="item"
                        :value="item"
                      >
                        {{ item }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                  <a-form-item label="模组型号">
                    <a-select
                      :value="draft.goodsId"
                      placeholder="请选择"
                      :disabled="!draft.producer"
                      @change="changeGoods"
                    >
                      <a-select-option
                        v-for="item in moduleOptions"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.model }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </template>

                <template v-else>
                  <a-form-item label="CPE型号">
                    <a-select
                      :value="draft.goodsId"
                      placeholder="请选择"
                      @change="changeGoods"
                    >
                      <a-select-option
                        v-for="item in cpeGoods"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.model }} / {{ item.comm }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </template>

                <a-form-item label="数量">
                  <a-input-number
                    v-model="draft.quantity"
                    :min="1"
                    :precision="0"
                    style="width: 120px"
                  />
                  <span class="unit-text">{{ quantityUnit }}</span>
                </a-form-item>
              </a-form>
            </a-col>

            <a-col :span="12">
              <div class="quote-side">
                <div class="section-title">报价与库存</div>
                <div v-if="!priceReady" class="hint">完成左侧点选后显示报价</div>
                <template v-else>
                  <a-table
                    size="middle"
                    :columns="priceColumns"
                    :data-source="priceRows"
                    :pagination="false"
                    row-key="key"
                  >
                    <template slot="quote" slot-scope="text, record">
                      <a-input-number
                        :value="record.quote"
                        :min="0"
                        :step="0.001"
                        style="width: 110px"
                        @change="value => changeQuote(record.key, value)"
                      />
                    </template>
                  </a-table>
                  <div class="stock-box">
                    <div v-if="stock.tracked">
                      库存 <span :class="{ 'stock-zero': stock.count === 0 }">{{ stock.count }} {{ quantityUnit }}</span>
                      <a-tag v-if="stock.count === 0" color="orange">库存不足</a-tag>
                    </div>
                    <div v-if="stock.note" class="hint">{{ stock.note }}</div>
                    <div v-for="item in stock.supplies" :key="item" class="hint">{{ item }}</div>
                  </div>
                  <div class="add-line-action">
                    <a-button type="primary" @click="addLine">添加本行</a-button>
                  </div>
                </template>
              </div>
            </a-col>
          </a-row>
        </div>

        <div class="quote-section">
          <div class="section-title">报价明细</div>
          <a-table
            size="middle"
            :columns="lineColumns"
            :data-source="lines"
            :pagination="false"
            row-key="id"
            :row-class-name="belowFloorRowClass"
          >
            <template slot="productDetail" slot-scope="text, record">
              <div class="product-detail">{{ productDetail(record) }}</div>
            </template>
            <template slot="lineQuantity" slot-scope="text, record">
              <a-input-number
                :min="1"
                :precision="0"
                :value="record.quantity"
                @change="value => changeLineQuantity(record, value)"
              />
            </template>
            <template slot="lineQuote" slot-scope="text, record">
              <div v-if="record.productType === 'sim' && record.packageKind !== 'common'" class="line-price">
                <span>套餐</span>
                <a-input-number :min="0" :step="0.001" :value="record.packageQuote" @change="value => changeLinePrice(record, 'packageQuote', value)" />
              </div>
              <template v-else-if="record.productType === 'sim'">
                <div class="line-price">
                  <span>套餐</span>
                  <a-input-number :min="0" :step="0.001" :value="record.packageQuote" @change="value => changeLinePrice(record, 'packageQuote', value)" />
                </div>
                <div class="line-price">
                  <span>卡板</span>
                  <a-input-number :min="0" :step="0.001" :value="record.cardQuote" @change="value => changeLinePrice(record, 'cardQuote', value)" />
                </div>
              </template>
              <div v-else class="line-price">
                <span>单价</span>
                <a-input-number :min="0" :step="0.001" :value="record.unitQuote" @change="value => changeLinePrice(record, 'unitQuote', value)" />
              </div>
            </template>
            <template slot="floorPrice" slot-scope="text, record">
              <div class="product-detail">{{ floorText(record) }}</div>
            </template>
            <template slot="stockAmount" slot-scope="text, record">
              <span :class="{ 'stock-zero': isZeroStock(record) }">{{ text }}</span>
            </template>
            <template slot="action" slot-scope="text, record">
              <a @click="removeLine(record.id)">移除</a>
            </template>
          </a-table>
          <div class="quote-footer">
            <div>货款合计 <span class="quote-total">{{ money(goodsTotal) }}</span> 元</div>
            <a-space>
              <a-button :disabled="lines.length === 0" :loading="exporting" @click="exportCustomerQuote">生成报价单</a-button>
              <a-button type="primary" :disabled="lines.length === 0" :loading="saving" @click="saveQuote">{{ editingQuoteId ? '保存修改' : '保存' }}</a-button>
            </a-space>
          </div>
        </div>

      </a-tab-pane>
      <a-tab-pane key="records" tab="报价记录">
        <a-form class="simiot-nested-search-form" @submit.prevent="searchRecords">
          <a-row :gutter="24">
            <a-col :span="6">
              <a-form-item label="客户名称">
                <a-input
                  v-model="recordQuery.customer_name"
                  allow-clear
                  @pressEnter="searchRecords"
                />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item>
                <a-space>
                  <a-button type="primary" html-type="submit">搜索</a-button>
                  <a-button @click="resetRecords">刷新</a-button>
                </a-space>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <a-table
          size="middle"
          :columns="savedColumns"
          :data-source="savedQuotes"
          :pagination="false"
          :loading="loadingSaved"
          :scroll="{ x: 'max-content' }"
          row-key="id"
          :row-class-name="belowFloorRowClass"
        >
          <div slot="code" slot-scope="code, record">
            <a @click="showQuoteDetail(record)">{{ code }}</a>
          </div>
          <template slot="savedAction" slot-scope="text, record">
            <a-space>
              <a @click="editQuote(record)">修改</a>
              <a @click="exportSavedQuote(record)">生成报价单</a>
            </a-space>
          </template>
        </a-table>
        <pagination
          v-show="pagination.total_count > 0"
          :total-count="pagination.total_count"
          :page.sync="recordQuery.page"
          :per-page.sync="recordQuery.per_page"
          @change="fetchSavedQuotes"
        />
      </a-tab-pane>
    </a-tabs>

    <a-modal
      :visible="detailVisible"
      :title="detail ? `报价详情 ${detail.code}` : '报价详情'"
      width="1400px"
      :footer="null"
      @cancel="detailVisible = false"
    >
      <template v-if="detail">
        <a-row class="detail-meta">
          <a-col :span="8">客户：{{ detail.customer_name || '未填写' }}</a-col>
          <a-col :span="8">客户类型：{{ detail.is_system_customer ? '系统客户' : '未建档客户' }}</a-col>
          <a-col :span="8">报价人：{{ detail.operator_name || '-' }}</a-col>
          <a-col :span="8">有效期：{{ detail.valid_days }} 天</a-col>
          <a-col :span="8">货款合计：{{ money(detail.goods_amount) }} 元</a-col>
          <a-col :span="8">保存时间：{{ detail.created_at }}</a-col>
          <a-col :span="24">备注：{{ detail.remark || '无' }}</a-col>
        </a-row>
        <a-table
          size="middle"
          :columns="detailColumns"
          :data-source="detail.items || []"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
          row-key="id"
          :row-class-name="belowFloorRowClass"
        >
          <template slot="productDetail" slot-scope="text, record">
            <div class="product-detail">{{ productDetail(record) }}</div>
          </template>
          <template slot="quoteText" slot-scope="text">
            <div class="product-detail">{{ text }}</div>
          </template>
          <template slot="floorPrice" slot-scope="text, record">
            <div class="product-detail">{{ floorText(record) }}</div>
          </template>
          <template slot="stockAmount" slot-scope="text, record">
            <span :class="{ 'stock-zero': isZeroStock(record) }">{{ text }}</span>
          </template>
        </a-table>
        <div class="detail-actions">
          <a-button @click="editQuote(detail)">修改</a-button>
          <a-button type="primary" :loading="exporting" @click="exportSavedQuote(detail)">生成报价单</a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { findAgentOptions } from '@/api/agent'
import { findCarrierTypeOptions } from '@/api/common_options'
import { createSalesQuote, downloadDraftSalesQuoteFile, downloadSalesQuoteFile, findSalesQuote, findSalesQuoteCatalog, findSalesQuotes, updateSalesQuote } from '@/api/sales_quote'
import { findTrafficSceneOptions } from '@/api/traffic_scene'
import Pagination from '@/components/Pagination'

function emptyDraft(productType) {
  return {
    productType: productType || 'sim',
    packageKind: 'common',
    poolType: undefined,
    addonType: undefined,
    voice: undefined,
    carrier: undefined,
    scene: undefined,
    size: undefined,
    productId: undefined,
    material: undefined,
    shape: undefined,
    orderCycles: undefined,
    goodsId: undefined,
    producer: undefined,
    quantity: 1,
    packageQuote: undefined,
    cardQuote: undefined,
    excessQuote: undefined,
    stopQuote: undefined,
    unitQuote: undefined
  }
}

function unique(list) {
  return list.filter((item, index) => list.indexOf(item) === index)
}

export default {
  name: 'SalesQuotes',
  components: {
    Pagination
  },
  data() {
    return {
      productTypes: [
        { value: 'sim', label: 'SIM卡' },
        { value: 'esim', label: 'eSIM' },
        { value: 'profile', label: 'Profile' },
        { value: 'module', label: '模组' },
        { value: 'cpe', label: 'CPE' }
      ],
      packageKinds: [
        { value: 'common', label: '通用套餐' },
        { value: 'pool', label: '流量总池包' },
        { value: 'addon', label: '加油包' }
      ],
      carrierTypeOptions: findCarrierTypeOptions(),
      trafficSceneOptions: [],
      loadingTrafficSceneOptions: false,
      simProducts: [],
      simStocks: [],
      poolProducts: [],
      addonProducts: [],
      esimGoods: [],
      profileGoods: [],
      moduleGoods: [],
      cpeGoods: [],
      activeTab: 'quote',
      editingQuoteId: 0,
      editingQuoteCode: '',
      detailVisible: false,
      detail: null,
      customerKeyword: '',
      customerSelectValue: undefined,
      agentOptions: [],
      savedQuotes: [],
      loadingSaved: false,
      saving: false,
      exporting: false,
      pagination: {
        total_count: 0
      },
      recordQuery: {
        customer_name: '',
        page: 1,
        per_page: 20
      },
      quote: {
        agentId: 0,
        customerName: '',
        validDays: 15,
        remark: ''
      },
      draft: emptyDraft('sim'),
      lines: [],
      nextLineId: 1,
      priceColumns: [
        { title: '项目', dataIndex: 'name' },
        { title: '标准价', dataIndex: 'standard' },
        { title: '底价（元）', dataIndex: 'floor' },
        { title: '对外报价（元）', dataIndex: 'quote', scopedSlots: { customRender: 'quote' }}
      ],
      lineColumns: [
        { title: '产品类型', customRender: (value, record) => this.productTypeLabel(record) },
        { title: '产品明细', dataIndex: 'summary', scopedSlots: { customRender: 'productDetail' }},
        { title: '数量', dataIndex: 'quantity', scopedSlots: { customRender: 'lineQuantity' }},
        { title: '对外报价（元）', dataIndex: 'quoteText', scopedSlots: { customRender: 'lineQuote' }},
        { title: '货款小计（元）', dataIndex: 'goodsAmountText' },
        { title: '底价（元）', scopedSlots: { customRender: 'floorPrice' }},
        { title: '库存', dataIndex: 'stockText', scopedSlots: { customRender: 'stockAmount' }},
        { title: '操作', dataIndex: 'action', scopedSlots: { customRender: 'action' }}
      ],
      detailColumns: [
        { title: '产品类型', width: 120, customRender: (value, record) => this.productTypeLabel(record) },
        { title: '产品明细', dataIndex: 'summary', width: 360, scopedSlots: { customRender: 'productDetail' }},
        { title: '数量', dataIndex: 'quantity', width: 80 },
        { title: '对外报价（元）', dataIndex: 'quote_text', width: 240, scopedSlots: { customRender: 'quoteText' }},
        { title: '货款小计（元）', dataIndex: 'goods_amount', width: 130, customRender: (value) => this.money(value) },
        { title: '底价（元）', width: 180, scopedSlots: { customRender: 'floorPrice' }},
        { title: '库存', dataIndex: 'stock_text', width: 120, scopedSlots: { customRender: 'stockAmount' }}
      ],
      savedColumns: [
        { title: '报价单号', dataIndex: 'code', width: 160, scopedSlots: { customRender: 'code' }},
        { title: '客户', dataIndex: 'customer_name', width: 180, customRender: (value) => value || '未填写' },
        { title: '客户类型', dataIndex: 'is_system_customer', width: 120, customRender: (value) => value ? '系统客户' : '未建档客户' },
        { title: '报价人', dataIndex: 'operator_name', width: 120 },
        { title: '明细', dataIndex: 'item_count', width: 80 },
        { title: '货款合计（元）', dataIndex: 'goods_amount', width: 140, customRender: (value) => this.money(value) },
        { title: '保存时间', dataIndex: 'created_at', width: 180 },
        { title: '操作', dataIndex: 'savedAction', width: 160, scopedSlots: { customRender: 'savedAction' }}
      ]
    }
  },
  created() {
    this.fetchCatalog()
  },
  computed: {
    showNewCustomerOption() {
      const keyword = this.$lodash.trim(this.customerKeyword)
      if (!keyword) {
        return false
      }
      return !this.agentOptions.some(item => item.name === keyword)
    },
    newCustomerValue() {
      return `new:${this.customerKeyword}`
    },
    selectedScene() {
      return this.trafficSceneOptions.find(item => item.id === this.draft.scene)
    },
    sceneSupportsVoice() {
      return !!(this.selectedScene && this.selectedScene.support_voice)
    },
    quotableSceneProducts() {
      if (this.sceneSupportsVoice) {
        return this.sceneProducts
      }
      return this.sceneProducts.filter(item => !item.voice)
    },
    sizeOptions() {
      return unique(this.quotableSceneProducts.map(item => item.size))
    },
    selectedSceneAttrs() {
      return this.selectedScene && this.selectedScene.attrs ? this.selectedScene.attrs : []
    },
    voiceOptions() {
      return unique(this.quotableSceneProducts.filter(item => item.size === this.draft.size).map(item => item.voice || ''))
    },
    needsVoiceChoice() {
      return this.sceneSupportsVoice && this.voiceOptions.length > 1
    },
    poolSizeOptions() {
      return unique(this.poolProducts.filter(item => item.poolType === this.draft.poolType).map(item => item.size))
    },
    addonSizeOptions() {
      return unique(this.addonProducts.filter(item => item.addonType === this.draft.addonType).map(item => item.size))
    },
    sceneProducts() {
      return this.simProducts.filter(item => item.carrier_type === this.draft.carrier && item.traffic_scene_id === this.draft.scene)
    },
    catalogProducts() {
      if (this.draft.packageKind === 'pool') {
        return this.poolProducts.filter(item => item.poolType === this.draft.poolType && item.size === this.draft.size)
      }
      if (this.draft.packageKind === 'addon') {
        return this.addonProducts.filter(item => item.addonType === this.draft.addonType && item.size === this.draft.size)
      }
      if (this.needsVoiceChoice && this.draft.voice === undefined) {
        return []
      }
      return this.quotableSceneProducts.filter(item => item.size === this.draft.size && (item.voice || '') === (this.draft.voice || ''))
    },
    matchedProducts() {
      return this.catalogProducts
    },
    selectedProduct() {
      if (this.draft.packageKind === 'pool') {
        return this.poolProducts.find(item => item.id === this.draft.productId)
      }
      if (this.draft.packageKind === 'addon') {
        return this.addonProducts.find(item => item.id === this.draft.productId)
      }
      return this.simProducts.find(item => item.id === this.draft.productId)
    },
    materialOptions() {
      return this.selectedProduct ? this.selectedProduct.cardTypes : []
    },
    selectedMaterial() {
      return this.materialOptions.find(item => item.name === this.draft.material)
    },
    shapeOptions() {
      return this.selectedMaterial ? this.selectedMaterial.shapes : []
    },
    orderCycleOptions() {
      if (!this.selectedProduct) {
        return []
      }
      return Object.keys(this.selectedProduct.tiers).map(item => Number(item))
    },
    selectedTier() {
      if (!this.selectedProduct || !this.draft.orderCycles) {
        return null
      }
      return this.selectedProduct.tiers[this.draft.orderCycles]
    },
    currentGoods() {
      if (this.draft.productType === 'esim') {
        return this.esimGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'profile') {
        return this.profileGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'module') {
        return this.moduleGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'cpe') {
        return this.cpeGoods.find(item => item.id === this.draft.goodsId)
      }
      return null
    },
    simpleMaterials() {
      return this.currentGoods && this.currentGoods.cardTypes ? this.currentGoods.cardTypes : []
    },
    simpleShapes() {
      const material = this.simpleMaterials.find(item => item.name === this.draft.material)
      return material ? material.shapes : []
    },
    producerOptions() {
      return unique(this.moduleGoods.map(item => item.producer))
    },
    moduleOptions() {
      return this.moduleGoods.filter(item => item.producer === this.draft.producer)
    },
    quantityUnit() {
      if (this.draft.productType === 'cpe') {
        return '台'
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return '个'
      }
      if (this.draft.productType === 'profile') {
        return '个'
      }
      return '张'
    },
    priceReady() {
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'common') {
        return !!(this.selectedTier && this.selectedMaterial && this.draft.shape)
      }
      if (this.draft.productType === 'sim') {
        return !!this.selectedTier
      }
      if (this.draft.productType === 'esim') {
        return !!(this.currentGoods && this.draft.material && this.draft.shape)
      }
      return !!this.currentGoods
    },
    priceRows() {
      if (!this.priceReady) {
        return []
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'common') {
        return [
          { key: 'packageQuote', name: '套餐费', standard: this.money(this.selectedTier.standard), floor: this.selectedTier.floor, quote: this.draft.packageQuote },
          { key: 'cardQuote', name: '卡板费（元/张）', standard: this.money(this.selectedMaterial.standard), floor: this.selectedMaterial.floor, quote: this.draft.cardQuote },
          { key: 'excessQuote', name: `超额资费（${this.selectedProduct.excess.unit}）`, standard: this.money(this.selectedProduct.excess.standard), floor: this.selectedProduct.excess.floor, quote: this.draft.excessQuote },
          { key: 'stopQuote', name: `停机保号（${this.selectedProduct.stop.unit}）`, standard: this.money(this.selectedProduct.stop.standard), floor: this.selectedProduct.stop.floor, quote: this.draft.stopQuote }
        ]
      }
      if (this.draft.productType === 'sim') {
        return [
          { key: 'packageQuote', name: '套餐费', standard: this.money(this.selectedTier.standard), floor: this.selectedTier.floor, quote: this.draft.packageQuote }
        ]
      }
      const price = this.simpleUnitPrice
      return [
        { key: 'unitQuote', name: '单价', standard: this.money(price.standard), floor: price.floor, quote: this.draft.unitQuote }
      ]
    },
    simpleUnitPrice() {
      if (this.draft.productType === 'esim') {
        return this.simpleMaterials.find(item => item.name === this.draft.material) || {}
      }
      return this.currentGoods || {}
    },
    stock() {
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'pool') {
        return { tracked: false, count: 0, supplies: [], note: '流量总池包按上架总包容量报价，不核卡库存。' }
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'addon') {
        return { tracked: false, count: 0, supplies: [], note: '加油包加在已有卡上，不核卡库存。' }
      }
      if (this.draft.productType === 'sim') {
        const voice = !!(this.selectedProduct && this.selectedProduct.voice)
        const found = this.simStocks.find(item => item.carrier_type === this.draft.carrier && item.traffic_scene_id === this.draft.scene && item.material === this.draft.material && item.shape === this.draft.shape && !!item.voice === voice)
        return {
          tracked: true,
          count: found ? found.count : 0,
          supplies: [],
          note: ''
        }
      }
      if (this.draft.productType === 'esim') {
        const goods = this.currentGoods
        const found = goods && goods.stocks.find(item => item.material === this.draft.material && item.shape === this.draft.shape)
        return { tracked: true, count: found ? found.count : 0, supplies: found ? found.supplies : [], note: '' }
      }
      if (this.draft.productType === 'profile') {
        return { tracked: true, count: this.currentGoods.stock, supplies: this.currentGoods.supplier ? [this.currentGoods.supplier] : [], note: '' }
      }
      if (this.currentGoods) {
        return { tracked: true, count: this.currentGoods.stock, supplies: [], note: '' }
      }
      return { tracked: true, count: 0, supplies: [], note: '' }
    },
    goodsTotal() {
      return this.lines.reduce((sum, item) => sum + item.goodsAmount, 0)
    }
  },
  methods: {
    fetchCatalog() {
      findSalesQuoteCatalog().then((res) => {
        if (res.code !== 0) {
          return
        }
        const data = res.data || {}
        this.simProducts = data.sim_products || []
        this.simStocks = data.sim_stocks || []
        this.poolProducts = data.pool_products || []
        this.addonProducts = data.addon_products || []
        this.esimGoods = data.esim_goods || []
        this.profileGoods = data.profile_goods || []
        this.moduleGoods = data.module_goods || []
        this.cpeGoods = data.cpe_goods || []
      })
    },
    changeTab(key) {
      if (key === 'records') {
        this.fetchSavedQuotes()
      }
    },
    fetchSavedQuotes() {
      this.loadingSaved = true
      findSalesQuotes(this.recordQuery).then((res) => {
        if (res.code === 0) {
          this.savedQuotes = res.data || []
          this.pagination = res.pagination || { total_count: 0 }
        }
        this.loadingSaved = false
      }).catch(() => {
        this.loadingSaved = false
      })
    },
    searchRecords() {
      this.recordQuery.page = 1
      this.fetchSavedQuotes()
    },
    resetRecords() {
      this.recordQuery.customer_name = ''
      this.recordQuery.page = 1
      this.fetchSavedQuotes()
    },
    searchAgents(name) {
      const keyword = this.$lodash.trim(name)
      this.customerKeyword = keyword
      if (!keyword) {
        this.agentOptions = []
        return
      }
      findAgentOptions({ name: keyword }).then((res) => {
        if (res.code === 0 && this.customerKeyword === keyword) {
          this.agentOptions = res.data || []
        }
      })
    },
    changeCustomer(value) {
      if (value === undefined || value === null || value === '') {
        this.quote.agentId = 0
        this.quote.customerName = ''
        this.customerSelectValue = undefined
        this.customerKeyword = ''
        this.agentOptions = []
        return
      }
      if (typeof value === 'string' && value.indexOf('new:') === 0) {
        this.quote.agentId = 0
        this.quote.customerName = value.slice(4)
        this.customerSelectValue = value
        return
      }
      const agent = this.agentOptions.find(item => item.id === value)
      this.quote.agentId = value
      this.quote.customerName = agent ? agent.name : ''
      this.customerSelectValue = value
    },
    isZeroStock(record) {
      const tracked = record.stockTracked !== undefined ? record.stockTracked : record.stock_tracked
      const count = record.stockCount !== undefined && record.stockCount !== null ? record.stockCount : record.stock_count
      return !!tracked && Number(count) === 0
    },
    belowFloorRowClass(record) {
      return record.belowFloor || record.below_floor ? 'quote-below-floor' : ''
    },
    floorValue(record, camel, snake) {
      const value = record[camel]
      if (value === undefined || value === null || value === '') {
        return record[snake]
      }
      return value
    },
    floorText(record) {
      const productType = record.productType || record.product_type
      const packageKind = record.packageKind || record.package_kind
      if (productType === 'sim' && packageKind && packageKind !== 'common') {
        return this.money(this.floorValue(record, 'packageFloor', 'package_floor'))
      }
      if (productType === 'sim') {
        return [
          `套餐 ${this.money(this.floorValue(record, 'packageFloor', 'package_floor'))}`,
          `卡板 ${this.money(this.floorValue(record, 'cardFloor', 'card_floor'))}`,
          `超额 ${this.money(this.floorValue(record, 'excessFloor', 'excess_floor'))}`,
          `停机 ${this.money(this.floorValue(record, 'stopFloor', 'stop_floor'))}`
        ].join('\n')
      }
      return this.money(this.floorValue(record, 'unitFloor', 'unit_floor'))
    },
    productTypeLabel(record) {
      const productType = record.productType || record.product_type
      const packageKind = record.packageKind || record.package_kind
      if (productType === 'sim') {
        if (packageKind === 'pool') {
          return '流量总池包'
        }
        if (packageKind === 'addon_voice') {
          return '语音加油包'
        }
        if (packageKind === 'addon' || packageKind === 'addon_flow') {
          return '流量加油包'
        }
        return 'SIM卡'
      }
      return { esim: 'eSIM', profile: 'Profile', module: '模组', cpe: 'CPE' }[productType] || ''
    },
    productDetail(record) {
      return record.summary || ''
    },
    storedPackageKind(draft) {
      if (draft.productType !== 'sim') {
        return ''
      }
      if (draft.packageKind === 'addon') {
        return draft.addonType === 'voice' ? 'addon_voice' : 'addon_flow'
      }
      return draft.packageKind
    },
    lineQuantityUnit(record) {
      const productType = record.productType || record.product_type
      const packageKind = record.packageKind || record.package_kind
      if (productType === 'cpe') {
        return '台'
      }
      if (productType === 'profile' || (productType === 'sim' && packageKind && packageKind !== 'common')) {
        return '个'
      }
      return '张'
    },
    money(value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      return Number(value).toFixed(3).replace(/\.?0+$/, '')
    },
    changeProductType(event) {
      const productType = event.target.value
      const next = emptyDraft(productType)
      next.quantity = this.draft.quantity
      this.draft = next
      this.trafficSceneOptions = []
    },
    changeCarrier(value) {
      this.draft.carrier = value
      this.trafficSceneOptions = []
      this.clearFrom('scene')
      if (value) {
        this.fetchTrafficSceneOptions(value)
      }
    },
    fetchTrafficSceneOptions(carrierType) {
      this.loadingTrafficSceneOptions = true
      findTrafficSceneOptions({ carrier_type: carrierType }).then((res) => {
        if (res.code === 0) {
          this.trafficSceneOptions = res.data || []
        }
        this.loadingTrafficSceneOptions = false
      }).catch(() => {
        this.loadingTrafficSceneOptions = false
      })
    },
    changeScene(value) {
      this.draft.scene = value
      this.clearFrom('size')
    },
    changePackageKind(event) {
      const quantity = this.draft.quantity
      this.draft = emptyDraft('sim')
      this.draft.packageKind = event.target.value
      this.draft.quantity = quantity
      this.trafficSceneOptions = []
    },
    changePoolType(event) {
      this.draft.poolType = event.target.value
      this.draft.size = undefined
      this.clearFrom('product')
    },
    changeAddonType(event) {
      this.draft.addonType = event.target.value
      this.draft.size = undefined
      this.clearFrom('product')
    },
    changeCatalogSize(value) {
      this.draft.size = value
      this.clearFrom('product')
      if (!value) {
        return
      }
      if (this.matchedProducts.length === 1) {
        this.applyProduct(this.matchedProducts[0].id)
      }
    },
    changeSize(value) {
      this.draft.size = value
      this.draft.voice = undefined
      this.clearFrom('product')
      if (!value) {
        return
      }
      if (!this.needsVoiceChoice) {
        this.draft.voice = this.voiceOptions[0] || ''
        if (this.matchedProducts.length === 1) {
          this.applyProduct(this.matchedProducts[0].id)
        }
      }
    },
    changeVoice(event) {
      this.draft.voice = event.target.value
      this.clearFrom('product')
      if (this.matchedProducts.length === 1) {
        this.applyProduct(this.matchedProducts[0].id)
      }
    },
    changeProduct(value) {
      this.applyProduct(value)
    },
    applyProduct(productId) {
      this.draft.productId = productId
      this.draft.material = undefined
      this.draft.shape = undefined
      this.draft.orderCycles = undefined
      this.draft.packageQuote = undefined
      this.draft.cardQuote = undefined
      this.draft.excessQuote = undefined
      this.draft.stopQuote = undefined
    },
    changeMaterial(event) {
      this.draft.material = event.target.value
      this.draft.shape = undefined
      this.draft.cardQuote = undefined
    },
    changeSimpleMaterial(event) {
      this.draft.material = event.target.value
      this.draft.shape = undefined
      this.draft.unitQuote = undefined
    },
    changeShape(event) {
      this.draft.shape = event.target.value
      this.fillQuotes()
    },
    changeOrderCycles(event) {
      this.draft.orderCycles = Number(event.target.value)
      this.fillQuotes()
    },
    changeGoods(value) {
      this.draft.goodsId = value
      this.draft.material = undefined
      this.draft.shape = undefined
      this.draft.unitQuote = undefined
      if (this.draft.productType === 'profile' || this.draft.productType === 'module' || this.draft.productType === 'cpe') {
        this.fillQuotes()
      }
    },
    changeProducer(value) {
      this.draft.producer = value
      this.draft.goodsId = undefined
      this.draft.unitQuote = undefined
    },
    clearFrom(field) {
      const order = ['scene', 'size', 'voice', 'product', 'material', 'shape', 'orderCycles']
      const index = order.indexOf(field)
      order.slice(index).forEach((key) => {
        if (key === 'product') {
          this.draft.productId = undefined
        } else {
          this.draft[key] = undefined
        }
      })
      this.draft.packageQuote = undefined
      this.draft.cardQuote = undefined
      this.draft.excessQuote = undefined
      this.draft.stopQuote = undefined
    },
    fillQuotes() {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common' && this.selectedTier) {
        this.draft.packageQuote = this.selectedTier.standard
        return
      }
      if (this.draft.productType === 'sim' && this.selectedTier && this.selectedMaterial) {
        this.draft.packageQuote = this.selectedTier.standard
        this.draft.cardQuote = this.selectedMaterial.standard
        this.draft.excessQuote = this.selectedProduct.excess.standard
        this.draft.stopQuote = this.selectedProduct.stop.standard
        return
      }
      if (this.priceReady && this.simpleUnitPrice.standard !== undefined) {
        this.draft.unitQuote = this.simpleUnitPrice.standard
      }
    },
    changeQuote(key, value) {
      this.draft[key] = value
    },
    addLine() {
      if (!this.priceReady || !this.draft.quantity) {
        this.$message.warning('请先完成点选并填写数量')
        return
      }
      this.pushLine(this.isBelowFloor())
    },
    isBelowFloor() {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return this.draft.packageQuote < this.selectedTier.floor
      }
      if (this.draft.productType === 'sim') {
        return this.draft.packageQuote < this.selectedTier.floor ||
          this.draft.cardQuote < this.selectedMaterial.floor ||
          this.draft.excessQuote < this.selectedProduct.excess.floor ||
          this.draft.stopQuote < this.selectedProduct.stop.floor
      }
      return this.draft.unitQuote < this.simpleUnitPrice.floor
    },
    pushLine(belowFloor) {
      const quantity = this.draft.quantity
      let goodsAmount = 0
      let quoteText = ''
      let summary = ''
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        goodsAmount = Number(this.draft.packageQuote) * quantity
        quoteText = `套餐费 ${this.money(this.draft.packageQuote)}元`
        summary = this.catalogSummary()
      } else if (this.draft.productType === 'sim') {
        goodsAmount = (Number(this.draft.packageQuote) + Number(this.draft.cardQuote)) * quantity
        quoteText = this.commonQuoteText({
          packageQuote: this.draft.packageQuote,
          cardQuote: this.draft.cardQuote,
          excessQuote: this.draft.excessQuote,
          stopQuote: this.draft.stopQuote,
          excessUnit: this.selectedProduct.excess.unit,
          stopUnit: this.selectedProduct.stop.unit
        })
        summary = this.commonSummary()
      } else {
        goodsAmount = Number(this.draft.unitQuote) * quantity
        quoteText = this.money(this.draft.unitQuote)
        summary = this.lineSummary()
      }
      const price = this.simpleUnitPrice || {}
      this.lines.push({
        id: this.nextLineId++,
        productType: this.draft.productType,
        packageKind: this.storedPackageKind(this.draft),
        excessUnit: this.selectedProduct && this.selectedProduct.excess ? this.selectedProduct.excess.unit : '',
        stopUnit: this.selectedProduct && this.selectedProduct.stop ? this.selectedProduct.stop.unit : '',
        summary,
        quantity,
        quoteText,
        goodsAmount,
        goodsAmountText: this.money(goodsAmount),
        belowFloor,
        stockCount: this.stock.tracked ? this.stock.count : 0,
        stockTracked: this.stock.tracked,
        stockText: this.stock.tracked ? `${this.stock.count}${this.quantityUnit}` : '不核库存',
        customerText: this.customerLineText(summary, quantity, goodsAmount),
        packageQuote: Number(this.draft.packageQuote || 0),
        cardQuote: Number(this.draft.cardQuote || 0),
        excessQuote: Number(this.draft.excessQuote || 0),
        stopQuote: Number(this.draft.stopQuote || 0),
        unitQuote: Number(this.draft.unitQuote || 0),
        packageFloor: this.selectedTier ? Number(this.selectedTier.floor || 0) : 0,
        cardFloor: this.selectedMaterial ? Number(this.selectedMaterial.floor || 0) : 0,
        excessFloor: this.selectedProduct && this.selectedProduct.excess ? Number(this.selectedProduct.excess.floor || 0) : 0,
        stopFloor: this.selectedProduct && this.selectedProduct.stop ? Number(this.selectedProduct.stop.floor || 0) : 0,
        unitFloor: Number(price.floor || 0)
      })
      this.keepSharedAndReset()
      this.$message.success('已添加本行，可更换产品类型继续添加')
    },
    commonSummary() {
      const carrier = this.carrierTypeOptions.find(item => item.value === this.draft.carrier)
      const material = [this.selectedMaterial.name, this.draft.shape].filter(Boolean).join(' / ')
      const business = [this.selectedProduct.business, `${this.draft.orderCycles}个周期`].filter(Boolean).join(' / ')
      return [
        `${carrier ? carrier.label : ''} / ${this.selectedProduct.name}`,
        material,
        business
      ].filter(Boolean).join('\n')
    },
    catalogSummary() {
      const cycles = `${this.draft.orderCycles}个周期`
      const name = this.selectedProduct ? this.selectedProduct.name : ''
      if (this.draft.packageKind === 'pool') {
        const poolType = this.draft.poolType === 'multi' ? '融合运营商' : '单一运营商'
        return `${poolType} / ${name} / ${cycles}`
      }
      return `${name} / ${cycles}`
    },
    lineSummary() {
      if (this.draft.productType === 'esim') {
        return `${this.currentGoods.name} / ${this.draft.material} / ${this.draft.shape}`
      }
      if (this.draft.productType === 'profile') {
        return this.currentGoods.name
      }
      if (this.draft.productType === 'module') {
        return `${this.currentGoods.producer} / ${this.currentGoods.model}`
      }
      return `${this.currentGoods.model} / ${this.currentGoods.comm}`
    },
    commonQuoteText(source) {
      const excessUnit = source.excessUnit || (source.excess && source.excess.unit) || ''
      const stopUnit = source.stopUnit || (source.stop && source.stop.unit) || ''
      return [
        `套餐费 ${this.money(source.packageQuote)}元`,
        `卡板费 ${this.money(source.cardQuote)}元/张`,
        `超额资费 ${this.money(source.excessQuote)}${excessUnit}`,
        `停机保号 ${this.money(source.stopQuote)}${stopUnit}`
      ].join('\n')
    },
    customerLineText(summary, quantity, goodsAmount) {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return [
          summary,
          `数量 ${quantity}个`,
          `套餐费 ${this.money(this.draft.packageQuote)}元`,
          `小计 ${this.money(goodsAmount)}元`
        ].join('\n')
      }
      if (this.draft.productType === 'sim') {
        return [
          summary,
          `数量 ${quantity}张`,
          `套餐费 ${this.money(this.draft.packageQuote)}元`,
          `卡板费 ${this.money(this.draft.cardQuote)}元/张`,
          `超额资费 ${this.money(this.draft.excessQuote)}${this.selectedProduct.excess.unit}`,
          `停机保号 ${this.money(this.draft.stopQuote)}${this.selectedProduct.stop.unit}`,
          `小计 ${this.money(goodsAmount)}元`
        ].join('\n')
      }
      return [
        summary,
        `数量 ${quantity}${this.quantityUnit}`,
        `单价 ${this.money(this.draft.unitQuote)}元`,
        `小计 ${this.money(goodsAmount)}元`
      ].join('\n')
    },
    keepSharedAndReset() {
      const productType = this.draft.productType
      const packageKind = this.draft.packageKind
      const poolType = this.draft.poolType
      const addonType = this.draft.addonType
      const carrier = this.draft.carrier
      const scene = this.draft.scene
      const producer = this.draft.producer
      this.draft = emptyDraft(productType)
      this.draft.packageKind = packageKind
      if (productType === 'sim' && packageKind === 'common') {
        this.draft.carrier = carrier
        this.draft.scene = scene
      }
      if (productType === 'sim' && packageKind === 'pool') {
        this.draft.poolType = poolType
      }
      if (productType === 'sim' && packageKind === 'addon') {
        this.draft.addonType = addonType
      }
      if (productType === 'module') {
        this.draft.producer = producer
      }
    },
    removeLine(id) {
      this.lines = this.lines.filter(item => item.id !== id)
    },
    changeLineQuantity(record, value) {
      if (!value) {
        return
      }
      record.quantity = value
      this.refreshLine(record)
    },
    changeLinePrice(record, field, value) {
      record[field] = value === null || value === undefined ? 0 : Number(value)
      this.refreshLine(record)
    },
    refreshLine(record) {
      const quantity = Number(record.quantity)
      if (record.productType === 'sim' && record.packageKind === 'common') {
        record.goodsAmount = (Number(record.packageQuote) + Number(record.cardQuote)) * quantity
        record.quoteText = this.commonQuoteText(record)
      } else if (record.productType === 'sim') {
        record.goodsAmount = Number(record.packageQuote) * quantity
        record.quoteText = `套餐费 ${this.money(record.packageQuote)}元`
      } else {
        record.goodsAmount = Number(record.unitQuote) * quantity
        record.quoteText = `单价 ${this.money(record.unitQuote)}元`
      }
      record.goodsAmountText = this.money(record.goodsAmount)
      record.belowFloor = this.lineBelowFloor(record)
      record.customerText = this.buildCustomerText(record)
    },
    lineBelowFloor(record) {
      if (record.productType === 'sim' && record.packageKind && record.packageKind !== 'common') {
        return Number(record.packageQuote) < Number(record.packageFloor)
      }
      if (record.productType === 'sim') {
        return Number(record.packageQuote) < Number(record.packageFloor) ||
          Number(record.cardQuote) < Number(record.cardFloor) ||
          Number(record.excessQuote) < Number(record.excessFloor) ||
          Number(record.stopQuote) < Number(record.stopFloor)
      }
      return Number(record.unitQuote) < Number(record.unitFloor)
    },
    buildCustomerText(record) {
      const unit = this.lineQuantityUnit(record)
      const lines = [record.summary, `数量 ${record.quantity}${unit}`]
      if (record.productType === 'sim' && record.packageKind === 'common') {
        lines.push(this.commonQuoteText(record))
      } else if (record.productType === 'sim') {
        lines.push(`套餐费 ${this.money(record.packageQuote)}元`)
      } else {
        lines.push(`单价 ${this.money(record.unitQuote)}元`)
      }
      lines.push(`小计 ${this.money(record.goodsAmount)}元`)
      return lines.join('\n')
    },
    exportCustomerQuote() {
      if (!this.quote.validDays) {
        this.$message.warning('请填写有效期')
        return
      }
      this.exporting = true
      const fileName = this.editingQuoteCode
        ? `报价单_${this.editingQuoteCode}_${this.quote.customerName || '未填写'}.xlsx`
        : `报价单_${this.quote.customerName || '未填写'}.xlsx`
      downloadDraftSalesQuoteFile(this.quotePayload()).then((res) => {
        this.saveQuoteFile(res, fileName)
        this.exporting = false
      }).catch(() => {
        this.exporting = false
      })
    },
    exportSavedQuote(record) {
      this.exporting = true
      const fileName = `报价单_${record.code || record.id}.xlsx`
      downloadSalesQuoteFile(record.id).then((res) => {
        this.saveQuoteFile(res, fileName)
        this.exporting = false
      }).catch(() => {
        this.exporting = false
      })
    },
    saveQuoteFile(data, fileName) {
      const blob = data instanceof Blob ? data : new Blob([data])
      if (blob.type && blob.type.indexOf('json') >= 0) {
        blob.text().then((text) => {
          let content = '生成报价单失败'
          try {
            const body = JSON.parse(text)
            if (body.message && body.message.content) {
              content = body.message.content
            }
          } catch (error) {
            content = '生成报价单失败'
          }
          this.$message.error(content)
        })
        return
      }
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      window.URL.revokeObjectURL(url)
    },
    quotePayload() {
      return {
        id: this.editingQuoteId || 0,
        agent_id: this.quote.agentId || 0,
        customer_name: this.quote.agentId ? '' : (this.quote.customerName || ''),
        valid_days: this.quote.validDays,
        remark: this.quote.remark || '',
        items: this.lines.map(item => ({
          product_type: item.productType,
          package_kind: item.packageKind,
          summary: item.summary,
          quantity: item.quantity,
          quote_text: item.quoteText,
          stock_count: item.stockCount,
          stock_tracked: item.stockTracked,
          stock_text: item.stockText,
          customer_text: item.customerText,
          package_quote: item.packageQuote,
          card_quote: item.cardQuote,
          excess_quote: item.excessQuote,
          stop_quote: item.stopQuote,
          unit_quote: item.unitQuote,
          package_floor: item.packageFloor,
          card_floor: item.cardFloor,
          excess_floor: item.excessFloor,
          stop_floor: item.stopFloor,
          unit_floor: item.unitFloor
        }))
      }
    },
    saveQuote() {
      if (!this.quote.validDays) {
        this.$message.warning('请填写有效期')
        return
      }
      this.saving = true
      const request = this.editingQuoteId ? updateSalesQuote(this.editingQuoteId, this.quotePayload()) : createSalesQuote(this.quotePayload())
      request.then((res) => {
        this.saving = false
        if (res.code === 0) {
          const saved = res.data || {}
          if (saved.id) {
            this.editingQuoteId = saved.id
          }
          if (saved.code) {
            this.editingQuoteCode = saved.code
          }
          this.$message.success(`报价已保存，单号 ${this.editingQuoteCode}`)
          this.fetchSavedQuotes()
        }
      }).catch(() => {
        this.saving = false
      })
    },
    showQuoteDetail(record) {
      findSalesQuote(record.id).then((res) => {
        if (res.code === 0) {
          this.detail = res.data
          this.detailVisible = true
        }
      })
    },
    editQuote(record) {
      findSalesQuote(record.id).then((res) => {
        if (res.code !== 0) {
          return
        }
        this.applyQuoteDetail(res.data)
        this.detailVisible = false
        this.activeTab = 'quote'
      })
    },
    applyQuoteDetail(detail) {
      this.editingQuoteId = detail.id
      this.editingQuoteCode = detail.code
      this.quote.validDays = detail.valid_days
      this.quote.remark = detail.remark || ''
      if (detail.agent_id) {
        this.agentOptions = [{ id: detail.agent_id, name: detail.customer_name }]
        this.customerSelectValue = detail.agent_id
        this.customerKeyword = ''
        this.quote.agentId = detail.agent_id
        this.quote.customerName = detail.customer_name
      } else if (detail.customer_name) {
        this.customerKeyword = detail.customer_name
        this.customerSelectValue = `new:${detail.customer_name}`
        this.agentOptions = []
        this.quote.agentId = 0
        this.quote.customerName = detail.customer_name
      } else {
        this.customerKeyword = ''
        this.customerSelectValue = undefined
        this.agentOptions = []
        this.quote.agentId = 0
        this.quote.customerName = ''
      }
      this.nextLineId = 1
      this.lines = (detail.items || []).map(item => ({
        id: this.nextLineId++,
        productType: item.product_type,
        packageKind: item.package_kind,
        summary: item.summary,
        quantity: item.quantity,
        quoteText: item.quote_text,
        goodsAmount: Number(item.goods_amount),
        goodsAmountText: this.money(item.goods_amount),
        belowFloor: item.below_floor,
        stockCount: item.stock_count,
        stockTracked: item.stock_tracked,
        stockText: item.stock_text,
        customerText: item.customer_text,
        packageQuote: Number(item.package_quote || 0),
        cardQuote: Number(item.card_quote || 0),
        excessQuote: Number(item.excess_quote || 0),
        stopQuote: Number(item.stop_quote || 0),
        unitQuote: Number(item.unit_quote || 0),
        packageFloor: Number(item.package_floor || 0),
        cardFloor: Number(item.card_floor || 0),
        excessFloor: Number(item.excess_floor || 0),
        stopFloor: Number(item.stop_floor || 0),
        unitFloor: Number(item.unit_floor || 0)
      }))
    },
    cancelEdit() {
      this.editingQuoteId = 0
      this.editingQuoteCode = ''
      this.quote.agentId = 0
      this.quote.customerName = ''
      this.quote.validDays = 15
      this.quote.remark = ''
      this.customerKeyword = ''
      this.customerSelectValue = undefined
      this.agentOptions = []
      this.lines = []
      this.nextLineId = 1
    }
  }
}
</script>

<style lang="less" scoped>
.sales-quote-page {
  /deep/ .ant-tabs-bar {
    margin-bottom: 16px;
  }
}

.quote-section {
  margin-bottom: 16px;
  padding: 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
}

.section-hint {
  margin: -8px 0 16px;
}

.editing-alert {
  margin-bottom: 16px;
}

.cancel-edit {
  margin-left: 12px;
}

.detail-meta {
  margin-bottom: 16px;
  padding: 8px 16px;
  line-height: 32px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
}

.detail-actions {
  margin-top: 16px;
  text-align: right;
}

.detail-actions .ant-btn + .ant-btn {
  margin-left: 8px;
}

.product-detail {
  white-space: pre-line;
}

.stock-zero {
  color: #cf1322;
}

.scene-attrs {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 20px;
}

.scene-attrs span {
  display: inline-block;
  margin: 0 8px 8px 0;
  padding: 0 8px;
  background: #f5f5f5;
  border-radius: 2px;
}

.line-price {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.line-price span {
  width: 36px;
}

.line-price .ant-input-number {
  width: 120px;
}

/deep/ .ant-table-tbody > tr.quote-below-floor > td {
  color: #cf1322;
  background: #fff1f0;
}

.section-title {
  margin-bottom: 16px;
  padding-left: 8px;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
  line-height: 16px;
  border-left: 3px solid #1890ff;
}

.quote-side {
  padding: 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
}

.unit-text {
  margin-left: 8px;
}

.hint {
  color: rgba(0, 0, 0, 0.45);
  line-height: 22px;
}

.quote-floor-hint {
  margin-top: 12px;
  color: #cf1322;
}

.stock-box {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e8e8e8;
}

.add-line-action {
  margin-top: 16px;
  text-align: right;
}

.quote-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px -16px -16px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.quote-total {
  color: #1890ff;
  font-size: 18px;
  font-weight: 500;
}

.card-select-box {
  margin: 0 10px 8px 0;
  border-radius: 0 !important;
  border: 1px solid #d9d9d9;
  text-align: center;

  &.card-type-box {
    min-width: 96px;
  }

  &.card-shape-box {
    min-width: 72px;
  }
}
</style>
